import { types, Instance } from 'mobx-state-tree'
import StoriesApi from '../../api/StoriesApi'
import { Alertable } from './helpers/Alertable'
import { Filterable, IFilterableArgs } from './helpers/Filterable'
import { LoadableStatus } from './helpers/LoadableStatus'
import { removeNullStringValue } from './utils'

import { snakify } from '../../decorators/textTools'
import { times, update } from 'lodash'
import Editable from './helpers/Editable'
import { ImageUrl } from './base/BaseObjects'
import { Dialogable } from './helpers'
import { RootAccessable } from './helpers/RootAccessable'

export const Story = types
  .compose(
    'Story',
    Editable,
    Alertable,
    Dialogable,
    types.model({
      id: types.identifierNumber,
      userId: types.maybeNull(types.number),
      conversationId: types.maybeNull(types.number),
      topicId: types.maybeNull(types.number),
      headline: types.string,
      firstName: types.maybeNull(types.string),
      lastName: types.maybeNull(types.string),
      createdAt: types.maybeNull(types.string),
      userLocation: types.maybeNull(types.string),
      asset: types.maybeNull(ImageUrl),
      prompt: types.maybeNull(types.string),
      visible: types.maybeNull(types.boolean),
      content: types.string,
      title: types.string,
      /**
       * The conversation's start time. Null for a story submitted against the
       * TOPIC rather than a single dinner -- a QR card has no dinner behind
       * it, so there is no date to carry.
       */
      startsAt: types.maybeNull(types.string),
      /**
       * The name typed into the form by someone who was not signed in. Null
       * whenever the story has a user, in which case `displayAuthorName` reads
       * `firstName`/`lastName` instead.
       */
      authorName: types.maybeNull(types.string),
      /**
       * How a moderator reaches whoever submitted a QR-card story -- to ask a
       * follow-up, to say it is live, or to honour a takedown.
       *
       * ⛔ ADMIN-ONLY, and that is enforced on the server, not here. It is on
       * `Admin::StoriesSerializer` and deliberately NOT on `StorySerializer`,
       * which is what the public story wall renders. So this is null for every
       * story that arrived through a public endpoint, and the only payload
       * that fills it comes through `Api::Admin::StoriesController#index`,
       * gated on super_admin/partner_admin.
       *
       * Declaring it on the shared `Story` model is therefore safe -- but
       * RENDERING it is not automatically safe. `StoryShowDialog` and
       * `StoryAuthor` are reached from the public topic page as well as from
       * the admin table, so this field must not be added to either. It belongs
       * in components that only an admin can reach.
       */
      authorEmail: types.maybeNull(types.string),
      slug: types.string,
      userAvatar: types.maybeNull(ImageUrl)
    })
  )
  .actions((self) => ({
    isHost: () => false,
    show: () => self.showDialog('showStory', { story: self }),
    updateVisibility: (visible: boolean) => {
      return StoriesApi.update(self.id, { visible }).then(({ response: { ok, statusText }, json }) => {
        if (ok) {
          self.updateMe(json)
          self.showAlert('Updated')
        }
      })
    }
  }))
  .views((self) => ({
    /**
     * The name to show for a story, and the value that goes into
     * `<meta name='author'>` on the story page.
     *
     * A story submitted by a signed-in user carries `firstName`/`lastName` off
     * the account. One submitted from a topic QR card has no account behind it
     * at all -- the server takes `author_name` off the form and hands it back
     * as `authorName` -- so that is the fallback.
     *
     * This used to be `${self.firstName} ${self.lastName}`, interpolated
     * unconditionally. Both fields are `maybeNull`, so a story with no user
     * rendered the literal string "null null" -- and that string was what went
     * into the author meta tag. Dropping the nils before joining is the fix;
     * an empty string is the honest answer when there is no name at all.
     *
     * Renamed from `authorName` because the model now carries a property by
     * that name -- the raw value off the wire -- and MST cannot have a view
     * and a property share one key.
     */
    get displayAuthorName() {
      return [self.firstName, self.lastName].filter(Boolean).join(' ') || self.authorName || ''
    },
    /**
     * The two links below are built from `window.location.origin` -- the
     * origin the app is actually being served from -- and never from a
     * REACT_APP_* value. Those are webpack compile-time substitutions baked in
     * per environment, so a constructed host is right in the environment it was
     * built for and silently wrong everywhere else. Same reasoning as
     * `Conversation.storyLink`.
     *
     * They previously read `window.location.host`, guarded by
     * `host.includes('http') ? '' : 'https://'`. `host` is `hostname:port` and
     * never contains a scheme, so that branch was dead and the scheme was in
     * effect hardcoded to https -- wrong on an http localhost, where the share
     * and og:url links came out pointing at a host that does not serve https.
     * `origin` already carries the scheme, so it needs none of that.
     *
     * There used to be a third, `conversationLink`. It had no caller anywhere
     * in the app -- every `conversation.conversationLink` in the tree is
     * `Conversation`'s own getter, a different model -- and it is the one link
     * of the three a topic-level story cannot build, since `conversationId` is
     * null for one. Removed rather than taught to return null.
     */
    get topicLink() {
      return `${window.location.origin}/${self.slug}`
    },
    get storyLink() {
      return `${window.location.origin}/${self.slug}/a/stories/${self.id}`
    }
  }))

const Word = types.model('Word', {
  text: types.string,
  value: types.number
})
export const Stories = types
  .compose(
    'Stories',
    LoadableStatus,
    Alertable,
    Filterable,
    RootAccessable,
    types.model({
      list: types.optional(types.array(Story), []),
      words: types.array(Word),
      /**
       * Which slice of the admin Stories table is on screen.
       *
       * `pending` is the approve-before-publish queue: stories that came in
       * from a topic QR card arrive `visible: false` and stay off the public
       * wall until an admin approves one. It is a scope on the list rather
       * than a separate store because it is the same rows, the same paging and
       * the same sorting -- exactly how `Topics` scopes active vs archived.
       */
      visibleScope: types.optional(types.enumeration('StoriesVisibleScope', ['all', 'pending']), 'all')
    })
  )
  .actions((self) => ({
    addToList(data: any) {
      removeNullStringValue(data)
      const found = self.list.find((o) => o.id === data.id)
      if (found) {
        Object.assign(found, data)
      } else {
        self.list.push(data)
      }
    },
    setList(data: any[], totalCount?: number) {
      data.forEach((d) => removeNullStringValue(d))
      self.list.replace(data)
      self.setTotalCount(totalCount)
    },
    setWords(data: any[]) {
      self.words.replace(data)
    },
    setVisibleScope(val: 'all' | 'pending') {
      self.visibleScope = val
    },
    removeFromList(id: number) {
      const index = self.list.findIndex((s) => s.id === id)
      if (index === -1) return

      self.list.splice(index, 1)
      // The pager's count comes from the server. One fewer row exists now, so
      // leaving it alone would report a page size the table cannot fill.
      self.setTotalCount(Math.max(0, (self.totalCount || 1) - 1))
    }
  }))
  .views((self) => ({
    /**
     * The rows the table should draw for the scope that is showing.
     *
     * KEPT even though the server-side filter now exists, for two reasons --
     * the second of which is not a stopgap at all.
     *
     * 1. It is what makes Approve feel immediate. `updateVisibility` writes
     *    the response back with `updateMe`, which flips `visible` to true on
     *    the row already in `list`; nothing refetches. Without this filter the
     *    story an admin just approved would sit in the Awaiting-approval tab
     *    labelled "Visible" until the page was reloaded. `Stories.test.ts`
     *    pins that behaviour.
     *
     * 2. The API change has NOT shipped yet. civicdinners_web#409 is open at
     *    the time of writing, and the two apps deploy separately -- the SPA by
     *    a manual push, the API by a pipeline promote -- so there is no
     *    ordering guarantee between them. Against today's production the admin
     *    index still drops `visible` silently at `params.permit`: it answers
     *    200 and filters nothing, which would put published stories in the
     *    approval queue and invite a moderator to "approve" them.
     *
     * Once #409 is live this filter removes nothing on load -- verified
     * against the branch, `Admin::StoriesService` applies `where(visible:)`
     * to the model BEFORE `ListService#process_query` paginates, so `total`
     * counts the filtered set and page 2 really is page 2 of the pending
     * ones. It is idempotent either way: it can only ever remove a visible
     * story from the pending tab, never add one.
     */
    get scopedList() {
      return self.visibleScope === 'pending' ? self.list.filter((s) => !s.visible) : self.list
    }
  }))
  .actions((self) => ({
    afterCreate: () => {
      self.setSortingId('id')
      self.setSortedPriority(true)
    },
    loadStories: (topic_id: number) => {
      const lisName = 'stories'
      if (self.isLoading(lisName)) {
        return
      }
      self.startLoading(lisName)

      return StoriesApi.getStoriesForTopic({ topic_id }).then(({ response: { ok, statusText }, json: { stories, total } }) => {
        if (ok) {
          self.setList(stories, total)
        } else {
          self.showAlert(statusText)
        }
        self.stopLoading(lisName)
      })
    },
    loadStoriesTable: (args: IFilterableArgs & { visible?: boolean }) => {
      const lisName = 'stories_table'
      if (self.isLoading(lisName)) {
        return
      }
      self.startLoading(lisName)

      const { selectedPartnerId } = self.getPartners()

      return StoriesApi.getAll({ ...args, ...(selectedPartnerId ? { partner_id: selectedPartnerId } : {}) }).then(
        ({ response: { ok, statusText }, json: { stories, total, words } }) => {
          if (ok) {
            self.setList(stories, total)
            self.setWords(words)
          } else {
            self.showAlert(statusText)
          }
          self.stopLoading(lisName)
        }
      )
    }
  }))
  .actions((self) => ({
    /**
     * Decline a story: destroy it.
     *
     * It lives on the collection rather than on `Story` because the point of
     * the call is what happens to the LIST -- the row leaves the
     * Awaiting-approval queue on success, without a refetch, the same
     * immediacy `updateVisibility` gives Approve. A `Story` cannot cleanly
     * remove itself: the same model is also a child of `Topic.topicStories`,
     * so walking to a parent would be guessing which list it is in.
     *
     * ⛔ This is not a replacement for `Story#updateVisibility`. Hiding a
     * published story and destroying an unreviewed one are different acts and
     * both stay available -- the hide/show toggle keeps its place in the All
     * tab.
     */
    deleteStory: (story: IStory) => {
      const { id, headline } = story

      return StoriesApi.destroy(id).then(({ response: { ok, statusText } }) => {
        if (ok) {
          self.removeFromList(id)
          self.showAlert(`Deleted "${headline}"`)
          return true
        }

        self.showAlert(statusText || 'Could not delete that story')
        return false
      })
    },
    loadFilter: ({ reset = false, clear = true } = {}) => {
      if (reset) {
        self.page = 0
        self.pageSize = 15
        self.search = ''
      }

      if (clear) {
        self.list.replace([])
      }

      const { search, pageSize: page_size, page, sortedId: sorted_id, sortedPriority: sorted_priority, visibleScope } = self
      return self.loadStoriesTable({
        search,
        page_size,
        page,
        sorted_id,
        sorted_priority,
        // `visible=false` is the moderation queue. The name is the server's:
        // `Admin::StoriesPolicy#permitted_attributes` lists `:visible`, and
        // `Admin::StoriesService` casts it with ActiveModel::Type::Boolean,
        // so the string "false" that queryString.stringify emits is read as
        // false. Absent means unfiltered -- `visible: true` is not the same
        // question as "everything", so the All tab sends nothing at all.
        ...(visibleScope === 'pending' ? { visible: false } : {})
      })
    }
  }))
export interface IStories extends Instance<typeof Stories> {}
export interface IStory extends Instance<typeof Story> {}
export default Stories
